import { Alert } from "react-native";

import { ResourceListScreen } from "~/components/composed/ResourceListScreen";
import { Button, ButtonText } from "~/components/ui";
import { useWorkspaceReadOnly } from "~/hooks/useWorkspaceReadOnly";
import { usePrimaryWorkspace, useWorkspaceMileage } from "~/hooks/workspace";
import { formatCurrency } from "~/utils/format";

const vehicleTypes = [
  { label: "Petrol", value: "petrol" },
  { label: "Diesel", value: "diesel" },
  { label: "Petrol hybrid", value: "petrol_hybrid" },
  { label: "Electric", value: "electric" },
  { label: "Other / custom rate", value: "other" },
];

export default function MileageScreen() {
  const workspace = usePrimaryWorkspace();
  const workspaceId = workspace.data?.id;
  const { readOnly } = useWorkspaceReadOnly(workspaceId);
  const mileage = useWorkspaceMileage(workspaceId);
  const totalKm = mileage.entries.reduce(
    (sum, entry) => sum + Number(entry.business_km),
    0,
  );
  const totalClaim = mileage.entries.reduce(
    (sum, entry) => sum + (entry.claim_cents ?? 0),
    0,
  );

  const fields = [
    { key: "travelledOn", label: "Travel date", placeholder: "YYYY-MM-DD" },
    { key: "vehicleName", label: "Vehicle", placeholder: "Brad's Corolla" },
    { key: "registration", label: "Registration", placeholder: "ABC123" },
    {
      key: "vehicleType",
      label: "Vehicle type",
      placeholder: "Petrol",
      options: vehicleTypes,
    },
    { key: "origin", label: "From", placeholder: "Office" },
    { key: "destination", label: "To", placeholder: "Client site" },
    { key: "purpose", label: "Business reason", placeholder: "Client meeting" },
    {
      key: "businessKm",
      label: "Business kilometres",
      placeholder: "24.5",
      keyboardType: "numeric" as const,
    },
    {
      key: "odometerStartKm",
      label: "Start odometer",
      placeholder: "41000.0",
      keyboardType: "numeric" as const,
    },
    {
      key: "odometerEndKm",
      label: "End odometer",
      placeholder: "41024.5",
      keyboardType: "numeric" as const,
    },
    {
      key: "rateTier",
      label: "IRD rate tier",
      placeholder: "Tier 1",
      options: [
        { label: "Tier 1 — first 14,000 total km", value: "1" },
        { label: "Tier 2 — after 14,000 total km", value: "2" },
      ],
    },
    {
      key: "rateCents",
      label: "Rate cents/km",
      placeholder: "Auto for 2026, or enter custom",
      keyboardType: "numeric" as const,
    },
    {
      key: "notes",
      label: "Notes",
      placeholder: "Parking recorded separately",
      multiline: true,
    },
  ];

  return (
    <ResourceListScreen
      title="Mileage log"
      subtitle="Record each business journey, its reason, distance and optional odometer readings. Current 2026 IRD kilometre rates fill automatically; verify the tier and rate for the relevant income year."
      primaryAction="Record journey"
      emptyTitle="Business journeys"
      emptyDescription="Record business trips as they happen so the year-end vehicle claim has evidence."
      fields={fields}
      requiredField="purpose"
      readOnly={readOnly}
      metrics={[
        { label: "Journeys", value: String(mileage.entries.length) },
        { label: "Business distance", value: `${totalKm.toFixed(1)} km` },
        { label: "Recorded claim", value: formatCurrency(totalClaim / 100) },
      ]}
      items={mileage.entries.map((entry) => ({
        id: entry.id,
        eyebrow: `${entry.travelled_on} · ${entry.vehicle_name}${entry.registration ? ` (${entry.registration})` : ""}`,
        title: entry.purpose,
        description:
          [entry.origin, entry.destination].filter(Boolean).join(" → ") ||
          undefined,
        meta: [
          `${Number(entry.business_km).toFixed(1)} km`,
          `Tier ${entry.rate_tier} @ ${Number(entry.rate_cents_per_km).toFixed(2)}c/km`,
          formatCurrency((entry.claim_cents ?? 0) / 100),
          `Income year ${entry.income_year}`,
        ],
        editValues: {
          travelledOn: entry.travelled_on,
          vehicleName: entry.vehicle_name,
          registration: entry.registration ?? "",
          vehicleType: entry.vehicle_type,
          origin: entry.origin ?? "",
          destination: entry.destination ?? "",
          purpose: entry.purpose,
          businessKm: String(entry.business_km),
          odometerStartKm:
            entry.odometer_start_km === null
              ? ""
              : String(entry.odometer_start_km),
          odometerEndKm:
            entry.odometer_end_km === null ? "" : String(entry.odometer_end_km),
          rateTier: String(entry.rate_tier),
          rateCents: String(entry.rate_cents_per_km),
          notes: entry.notes ?? "",
        },
        actions: readOnly ? undefined : (
          <Button
            compact
            variant="outline"
            onPress={() =>
              Alert.alert("Delete mileage entry?", entry.purpose, [
                { text: "Cancel", style: "cancel" },
                {
                  text: "Delete",
                  style: "destructive",
                  onPress: () => mileage.remove.mutate(entry.id),
                },
              ])
            }
          >
            <ButtonText>Delete</ButtonText>
          </Button>
        ),
      }))}
      loading={workspace.isLoading || mileage.isLoading}
      error={
        mileage.error ??
        mileage.create.error ??
        mileage.update.error ??
        mileage.remove.error
      }
      creating={mileage.create.isPending}
      updating={mileage.update.isPending}
      onCreate={(values) => mileage.create.mutateAsync(values)}
      onUpdate={(id, values) => mileage.update.mutateAsync({ id, values })}
    />
  );
}
